import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { apiFetch, ApiError } from "@/lib/api";

// Auth state is backed by a real backend session. Flow:
//
//   1. Login form → POST /api/auth/login.
//   2. Backend verifies bcrypt password against users.json
//      on the host-only secrets dir, issues an HttpOnly
//      signed session cookie (oc_session), returns
//      { user, customerId, role }.
//   3. Login.tsx calls `login()` → flips `authenticated`
//      and persists metadata to localStorage (display-only;
//      the real session lives in the HttpOnly cookie).
//   4. On page reload, App calls `rehydrate()` which hits
//      /api/auth/me. If the cookie is still valid we stay
//      logged in, otherwise we drop to logged-out.
//
// The localStorage copy is intentionally display-only. Every
// API request is validated against the cookie by requireAuth
// + requireTenantAccess middleware, so tampering with
// localStorage doesn't grant access — the server rejects it.

/**
 * Why the app shows signed out. Every reason shows the login screen; the
 * difference matters to the iPhone app's web screens, which tell the app
 * only about a real sign-out (native/embedded.ts tellsNativeSignedOut).
 *   expired    /api/auth/me answered 401: no cookie, or one that is no
 *              longer valid.
 *   logout     the user signed out in this page.
 *   unchecked  the check failed some other way (offline, a 5xx, a proxy's
 *              error page): the session may be fine.
 */
export type SignedOutReason = "expired" | "logout" | "unchecked";

interface AuthState {
  authenticated: boolean;
  user: string | null;
  customerId: string | null;
  role: "customer" | "admin" | "family" | null;
  /** Family (child) sessions only: the ONE agent this login may reach.
   * Null for operators. The child shell uses it as its target agent. */
  agent: string | null;
  /** False before the first /api/auth/me call has resolved,
   * true afterwards (regardless of outcome). Lets the app
   * show a neutral loading state instead of flashing
   * Login → app or app → Login. */
  hydrated: boolean;
  /** Why `authenticated` is false after a check or a logout; null while
   * signed in or before the first check. Per page, never persisted. */
  signedOut: SignedOutReason | null;
  /** True after checkSession found the session gone (the login page says
   * so). Per page, never persisted; login() clears it. */
  sessionEnded: boolean;

  login: (
    user: string,
    customerId: string | null,
    role: "customer" | "admin" | "family",
    agent?: string | null,
  ) => void;
  logout: () => Promise<void>;
  rehydrate: () => Promise<void>;
  /** Asks /api/auth/me whether the session still holds (after some other
   * call answered 401). At most once every 15 s. Only a 401 from /me signs
   * out; success or any other failure (offline, a restart) changes nothing. */
  checkSession: () => Promise<void>;
}

interface MeResponse {
  user: string;
  customerId: string | null;
  role: "customer" | "admin" | "family";
  agent: string | null;
  exp: number;
}

const SIGNED_OUT = {
  authenticated: false,
  user: null,
  customerId: null,
  role: null,
  agent: null,
  hydrated: true,
} as const;

const CHECK_EVERY_MS = 15_000;
let lastCheck = 0;

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      authenticated: false,
      user: null,
      customerId: null,
      role: null,
      agent: null,
      hydrated: false,
      signedOut: null,
      sessionEnded: false,

      login: (user, customerId, role, agent = null) =>
        set({
          authenticated: true,
          user,
          customerId,
          role,
          agent,
          hydrated: true,
          signedOut: null,
          sessionEnded: false,
        }),

      logout: async () => {
        // Best-effort — even if the POST fails we still clear
        // local state so the UI immediately reflects intent.
        try {
          await apiFetch<{ ok: boolean }>("/api/auth/logout", {
            method: "POST",
          });
        } catch {
          /* ignore */
        }
        set({
          authenticated: false,
          user: null,
          customerId: null,
          role: null,
          agent: null,
          hydrated: true,
          signedOut: "logout",
        });
      },

      rehydrate: async () => {
        try {
          const me = await apiFetch<MeResponse>("/api/auth/me");
          set({
            authenticated: true,
            user: me.user,
            customerId: me.customerId,
            role: me.role,
            agent: me.agent ?? null,
            hydrated: true,
            signedOut: null,
          });
        } catch (e) {
          // 401 → missing or expired cookie. Any other error
          // (network, 500) is treated the same way: fall back
          // to logged-out and let the user log in fresh. Which
          // of the two it was is kept for the iPhone app, which
          // must not hear "signed out" for a network error.
          if (e instanceof ApiError || e instanceof Error) {
            set({
              authenticated: false,
              user: null,
              customerId: null,
              role: null,
              agent: null,
              hydrated: true,
              signedOut: e instanceof ApiError && e.status === 401 ? "expired" : "unchecked",
            });
          }
        }
      },

      checkSession: async () => {
        if (!get().authenticated) return;
        const now = Date.now();
        if (now - lastCheck < CHECK_EVERY_MS) return;
        lastCheck = now;
        try {
          await apiFetch<MeResponse>("/api/auth/me");
        } catch (e) {
          // A flaky network or a restarting server must never sign anyone out.
          if (e instanceof ApiError && e.status === 401) {
            set({ ...SIGNED_OUT, signedOut: "expired", sessionEnded: true });
          }
        }
      },
    }),
    {
      name: "corvus:auth",
      storage: createJSONStorage(() => localStorage),
      // Don't persist `hydrated` — it's derived per-boot
      // from a network call and must always start false.
      partialize: (state) => ({
        authenticated: state.authenticated,
        user: state.user,
        customerId: state.customerId,
        role: state.role,
      }),
    },
  ),
);
